// src/App.tsx
import React, { useEffect, useState } from 'react'
import { Routes, Route, useNavigate, Navigate, useLocation, useParams } from 'react-router-dom'
import { Users, BriefcaseBusiness, Globe2 } from 'lucide-react'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import { auth } from './firebase'
import Login from './pages/Login'
import { api } from './api'
import { useOnline } from './hooks/useOnline'
import Spinner from './components/Spinner'
import OauthCallback from './pages/OauthCallback'

// existing pages (unchanged)
import Inbox from './pages/Inbox'
import AvailabilityPage from './pages/Availability'
import AppointmentsPage from './pages/Appointments'
import InboxDetail from './pages/InboxDetail'

// ✅ NEW structure imports (generic match + requests + register)
import ResultsPage from './pages/match/ResultsPage'
import RequestsPage from './pages/match/RequestsPage'
import RequestsDetail from './pages/match/RequestsDetail'
import RegisterDoctor from './pages/register/RegisterDoctor'
import RegisterEducation from './pages/register/RegisterEducation'
import RegisterProperty from './pages/register/RegisterProperty'
import RegisterTech from './pages/register/RegisterTech'
import RegisterFinancial from './pages/register/RegisterFinancial'
import RegisterAllied from './pages/register/RegisterAllied'
import RegisterLegal from './pages/register/RegisterLegal'
import RegisterMigration from './pages/register/RegisterMigration'
import RegisterClient from './pages/register/RegisterClient'
import AdminProviderEditor from './pages/AdminProviderEditor'

// homes & chrome
import ClientsHome from './pages/ClientsHome'
import AiSearchPage from './pages/AiSearchPage'
import ProvidersHome from './pages/ProvidersHome'
import ProviderProfilePage from './pages/ProviderProfilePage'
import ExperienceSwitcher from './components/ExperienceSwitcher'
import BottomDock from './components/BottomDock'
import ChatDock from './components/ChatDock'
import { resolveProfessionParam } from './professions'
import { UI_LANGUAGE_OPTIONS, useUiLanguage, type UiLanguageCode } from './i18n'

const LS_BACKEND_TOKEN = 'eucamd.backend.jwt'

const getBackendToken = () => {
  try {
    return window.localStorage.getItem(LS_BACKEND_TOKEN)
  } catch {
    return null
  }
}

const clearBackendToken = () => {
  try {
    window.localStorage.removeItem(LS_BACKEND_TOKEN)
  } catch {
    // ignore storage errors
  }
}

const decodeEucaExpMs = (token: string | null) => {
  if (!token || !token.startsWith('EUCA ')) return null
  const raw = token.slice('EUCA '.length)
  const [payloadB64] = raw.split('.')
  if (!payloadB64) return null
  try {
    let padded = payloadB64.replace(/-/g, '+').replace(/_/g, '/')
    const padLen = padded.length % 4
    if (padLen) padded += '='.repeat(4 - padLen)
    const json = JSON.parse(atob(padded))
    const expSec = Number(json?.exp)
    return Number.isFinite(expSec) ? expSec * 1000 : null
  } catch {
    return null
  }
}

const hasValidBackendToken = () => {
  const token = getBackendToken()
  if (!token) return false
  const expMs = decodeEucaExpMs(token)
  return !expMs || expMs > Date.now()
}
import AccountPage from './pages/Account'
import { NotificationsProvider } from './notifications/NotificationsContext'
import { AccountAvatarContext } from './contexts/AccountAvatarContext'

import '@fullcalendar/common/main.css'
import '@fullcalendar/daygrid/main.css'
import '@fullcalendar/timegrid/main.css'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string)
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-PH8P0Y2GK7'
type Role = 'patient' | 'specialist' | undefined

type RequireAuthProps = {
  children: React.ReactNode
  allowUnregistered?: boolean
  requireCompleteClient?: boolean
  tolerantRegistration?: boolean
  loading: boolean
  user: any
  profile: any
}

const MatchRedirect = () => {
  const { profession } = useParams()
  const params = new URLSearchParams()
  if (profession) {
    params.set('profession', resolveProfessionParam(profession))
  }
  const qs = params.toString()
  return <Navigate to={`/clients${qs ? `?${qs}` : ''}`} replace />
}

const AvailabilityRedirect = () => {
  const loc = useLocation()
  return <Navigate to={`/calendar${loc.search}${loc.hash}`} replace />
}

const DashboardRedirect = () => {
  const loc = useLocation()
  const { selectionId } = useParams()
  const base = selectionId ? `/dashboard/${selectionId}` : '/dashboard'
  return <Navigate to={`${base}${loc.search}${loc.hash}`} replace />
}

const ResultsGate: React.FC<Pick<RequireAuthProps, 'loading' | 'user' | 'profile'>> = ({
  loading,
  user,
  profile,
}) => {
  const { profession } = useParams()
  const loc = useLocation()
  if (loading) {
    return (
      <div className="container">
        <Spinner label="Checking session…" />
      </div>
    )
  }
  const qs = new URLSearchParams(loc.search || '')
  const allowGuest = !user
  if (allowGuest) return <ResultsPage />
  return (
    <RequireAuth
      loading={loading}
      user={user}
      profile={profile}
      requireCompleteClient
    >
      <ResultsPage />
    </RequireAuth>
  )
}

const RequireAuth: React.FC<RequireAuthProps> = ({
  children,
  allowUnregistered = false,
  requireCompleteClient = false,
  tolerantRegistration = false,
  loading,
  user,
  profile,
}) => {
  const loc = useLocation()
  if (loading) {
    return (
      <div className="container">
        <Spinner label="Checking session…" />
      </div>
    )
  }
  if (!user) {
    const backendTok = getBackendToken()
    if (backendTok) {
      const expMs = decodeEucaExpMs(backendTok)
      if (!expMs || expMs > Date.now()) {
        return <>{children}</>
      }
    }
    const next = `${loc.pathname}${loc.search || ''}${loc.hash || ''}`
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />
  }
  if (!allowUnregistered && requireCompleteClient) {
    const needs = profile?.needsRegistration
    if (needs !== false) {
      const next = `${loc.pathname}${loc.search || ''}`
      return <Navigate to={`/register/client?next=${encodeURIComponent(next)}`} replace />
    }
  }
  return <>{children}</>
}

const CLIENT_REQUIRED_FIELDS = ['email', 'phone'] as const
const hasCompleteClientProfile = (profile: any | null | undefined) => {
  if (!profile) return false
  const version = Number(profile.clientProfileVersion || 0)
  if (version >= 1) return true
  const hasNames =
    (profile.firstName && String(profile.firstName).trim() &&
      profile.lastName && String(profile.lastName).trim()) ||
    (profile.name && String(profile.name).trim())
  if (!hasNames) return false
  return CLIENT_REQUIRED_FIELDS.every((field) => {
    const val = profile[field]
    if (val === null || val === undefined) return false
    if (typeof val === 'number') return true
    return String(val).trim().length > 0
  })
}

const ProviderRedirect: React.FC = () => {
  const { providerSlug } = useParams<{ providerSlug: string }>()
  return <Navigate to={`/profile/${providerSlug || ''}`} replace />
}

export default function App() {
  const nav = useNavigate()
  const loc = useLocation()
  const { language, setLanguage } = useUiLanguage()
  const online = useOnline()
  const STORAGE_EXPERIENCE_KEY = 'euca.lastExperience'
  useEffect(() => {
    if (!GA_MEASUREMENT_ID) return
    if (typeof window === 'undefined') return
    const existing = document.querySelector(`script[data-ga-id=\"${GA_MEASUREMENT_ID}\"]`)
    if (!existing) {
      const s = document.createElement('script')
      s.async = true
      s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
      s.setAttribute('data-ga-id', GA_MEASUREMENT_ID)
      document.head.appendChild(s)
    }
    const w = window as any
    w.dataLayer = w.dataLayer || []
    w.gtag =
      w.gtag ||
      function gtag() {
        w.dataLayer.push(arguments)
      }
    w.gtag('js', new Date())
    w.gtag('config', GA_MEASUREMENT_ID)
  }, [])

  useEffect(() => {
    const w = window as any
    if (!GA_MEASUREMENT_ID || !w.gtag) return
    w.gtag('config', GA_MEASUREMENT_ID, {
      page_path: `${loc.pathname}${loc.search}${loc.hash}`,
    })
  }, [loc.pathname, loc.search, loc.hash])

  // 👇 derive initial experience from the URL
  const providerPrefixes = ['/professionals', '/work', '/dashboard', '/inbox', '/calendar', '/availability', '/specialist']
  const clientPrefixes = ['/match', '/clients', '/ai-search', '/requests', '/start', '/clientdashboard']
  const deriveExperience = (pathname: string, prev?: 'client' | 'provider'): 'client' | 'provider' => {
    if (providerPrefixes.some((p) => pathname.startsWith(p))) return 'provider'
    if (clientPrefixes.some((p) => pathname.startsWith(p))) return 'client'
    return prev || 'client'
  }

  const initialExperience: 'client' | 'provider' = deriveExperience(loc.pathname)

  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState<any>(null)
  const [profile, setProfile] = useState<any>(null)
  const [experience, setExperience] = useState<'client' | 'provider'>(initialExperience)
  const [accountAvatarUrl, setAccountAvatarUrl] = useState<string | null>(null)
  const [accountUserId, setAccountUserId] = useState<string | null>(null)
  const [showExperiencePrompt, setShowExperiencePrompt] = useState(false)
  const pathRef = React.useRef(loc.pathname)

  const persistExperience = (next: 'client' | 'provider') => {
    try {
      localStorage.setItem(STORAGE_EXPERIENCE_KEY, next)
    } catch {
      /* ignore */
    }
  }

  const getPersistedExperience = (): 'client' | 'provider' | null => {
    try {
      const val = localStorage.getItem(STORAGE_EXPERIENCE_KEY)
      if (val === 'client' || val === 'provider') return val
    } catch {
      /* ignore */
    }
    return null
  }

  const goHomeForExperience = (exp: 'client' | 'provider') => {
    nav(exp === 'provider' ? '/dashboard' : '/clientdashboard', { replace: true })
  }

  // keep experience in sync when deep-linking
  useEffect(() => {
    pathRef.current = loc.pathname
    setExperience(prev => {
      if (loc.pathname.startsWith('/appointments')) {
        const params = new URLSearchParams(loc.search)
        if (params.get('view') === 'provider' || params.get('view') === 'specialist') return 'provider'
        if (params.get('view') === 'patient') return 'client'
      }
      return deriveExperience(loc.pathname, prev)
    })
  }, [loc.pathname, loc.search])

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u)

      // not logged in → if a backend token exists, try to hydrate profile; otherwise go to /login
      if (!u) {
        setProfile(null)
        const backendTok = getBackendToken()
        if (backendTok) {
          setLoading(true)
          try {
            const appProfile = await api.me()
            if (appProfile) {
              setProfile(appProfile)
            }
          } catch {
            try {
              window.localStorage.removeItem(LS_BACKEND_TOKEN)
            } catch {}
            const path = pathRef.current
            if (!['/login', '/oauth/callback'].includes(path) && !path.startsWith('/results')) {
              nav('/login')
            }
          } finally {
            setLoading(false)
          }
          return
        }
        const path = pathRef.current
        if (!['/login', '/oauth/callback'].includes(path) && !path.startsWith('/results')) {
          nav('/login')
        }
        setLoading(false)
        return
      }

      setLoading(true)
      let appProfile: any = null
      try {
        // Ensure we have a fresh token before hitting stack APIs to avoid preflight/403 noise
        try {
          await auth.currentUser?.getIdToken(true)
        } catch {
          /* ignore */
        }
        // Only fetch the shared app profile at boot; stack-specific profiles load on demand
        appProfile = await api.me()
      } catch {
        /* ignore */
      }

      try {
        const merged: any = appProfile ? { ...appProfile } : {}
        const roleSet = new Set<string>()
        const addRoles = (source?: any) => {
          if (!source) return
          if (Array.isArray(source.roles)) source.roles.forEach((r: string) => roleSet.add(r))
          else if (source.role) roleSet.add(source.role)
        }
        addRoles(appProfile)
        if (roleSet.size) merged.roles = Array.from(roleSet)

        const fallbackNeedsRegistration = !hasCompleteClientProfile(appProfile)
        const needsRegistration = appProfile
          ? (appProfile.needsRegistration === false ? false : fallbackNeedsRegistration)
          : true
        merged.needsRegistration = needsRegistration

        setProfile(merged)

        const params = new URLSearchParams(loc.search)
        const next = params.get('next')
        const expParam = params.get('exp')

        if (next) {
          if (merged?.needsRegistration === false) {
            nav(next, { replace: true })
          } else {
            nav(`/register/client?next=${encodeURIComponent(next)}`, { replace: true })
          }
        } else if (expParam === 'provider' || expParam === 'client') {
          const exp = expParam === 'provider' ? 'provider' : 'client'
          setExperience(exp)
          goHomeForExperience(exp)
        } else if (['/', '/login', '/oauth/callback'].includes(pathRef.current)) {
          const persisted = getPersistedExperience()
          const choice: 'client' | 'provider' = persisted || 'client'
          setShowExperiencePrompt(false)
          setExperience(choice)
          goHomeForExperience(choice)
        }
      } catch (e) {
        setProfile(null)
      } finally {
        setLoading(false)
      }
    })
  }, [nav])

  const effectiveRole = React.useMemo(() => {
    if (!profile) return undefined
    if (Array.isArray(profile.roles)) {
      if (profile.roles.includes('specialist')) return 'specialist'
      if (profile.roles.includes('practice')) return 'practice'
      if (profile.roles.includes('patient')) return 'patient'
    }
    return profile?.role
  }, [profile])

  const roleSet = React.useMemo(() => {
    const next = new Set<string>()
    if (Array.isArray(profile?.roles)) profile!.roles.forEach((r: string) => next.add(r))
    if (profile?.role) next.add(profile.role)
    return next
  }, [profile])

  const canPatient = roleSet.has('patient')
  const canSpecialist = roleSet.has('specialist') || roleSet.has('practice')

  const navRole: Role = React.useMemo(() => {
    if (experience === 'client' && canPatient) return 'patient'
    if (experience === 'provider' && canSpecialist) return 'specialist'
    if (canPatient) return 'patient'
    if (canSpecialist) return 'specialist'
    return undefined
  }, [experience, canPatient, canSpecialist])
  const hideDockRoutes =
    loc.pathname === '/login' ||
    loc.pathname === '/oauth/callback'
  const showBottomDock = (Boolean(user) || hasValidBackendToken()) && !hideDockRoutes

  useEffect(() => {
    setAccountAvatarUrl(profile?.avatarUrl || null)
  }, [profile?.avatarUrl])

  useEffect(() => {
    const nextId = profile?.userId || user?.uid || null
    setAccountUserId(nextId || null)
  }, [profile?.userId, user])

  if (loading) {
    return (
      <div className="container">
        <Spinner label="Loading app…" />
      </div>
    )
  }

  const onLogout = async () => {
    clearBackendToken()
    try {
      await signOut(auth)
    } catch {
      // ignore Firebase sign-out errors and still clear local session
    }
    setProfile(null)
    setUser(null)
    setShowExperiencePrompt(false)
    nav('/login', { replace: true })
  }

  function renderExperiencePrompt() {
    const cards: Array<{
      id: 'client' | 'provider'
      title: string
      body: string
      icon: React.ReactNode
      cta: string
    }> = [
      {
        id: 'client',
        title: 'Book as a client',
        body: 'Find vetted specialists, compare options, and book in a few taps.',
        icon: <Users size={22} />,
        cta: 'Continue as client',
      },
      {
        id: 'provider',
        title: 'Work as a provider',
        body: 'List your services, manage requests, and keep availability current.',
        icon: <BriefcaseBusiness size={22} />,
        cta: 'Continue as provider',
      },
    ]

    const choose = (id: 'client' | 'provider') => {
      setExperience(id)
      persistExperience(id)
      setShowExperiencePrompt(false)
      goHomeForExperience(id)
    }

    const brandLogo = '/logo.png'

    return (
      <div className="container" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
        <div
          className="card"
          style={{
            width: '100%',
            maxWidth: 900,
            margin: '0 auto',
            padding: '2.25rem',
            background: '#f8fafc',
            color: '#0f172a',
            boxShadow: '0 18px 50px rgba(15, 23, 42, 0.12)',
            border: '1px solid #e2e8f0',
            borderRadius: 18,
          }}
        >
          <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src={brandLogo} alt="EUCA Match" width={44} height={44} style={{ borderRadius: 12 }} />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: 13, letterSpacing: 0.5, color: '#475569', textTransform: 'uppercase' }}>
                Welcome to
              </span>
              <span style={{ fontWeight: 800, fontSize: 20, color: '#0f172a' }}>EUCA Match</span>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '2rem', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 260px' }}>
              <h1 style={{ margin: '.25rem 0 0.5rem', fontSize: 38, lineHeight: 1.1, fontWeight: 800 }}>
                How do you want to start?
              </h1>
            </div>
            <div style={{ flex: '1 1 260px', display: 'grid', gap: '1rem' }}>
              {cards.map((card) => (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => choose(card.id)}
                  style={{
                    textAlign: 'left',
                    padding: '1rem 1.1rem',
                    borderRadius: 12,
                    border: '1px solid #e2e8f0',
                    background: '#ffffff',
                    color: '#0f172a',
                    display: 'grid',
                    gap: 6,
                    boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
                    transition: 'transform 120ms ease, box-shadow 120ms ease',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 12,
                        background: '#f1f5f9',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {card.icon}
                    </span>
                    <div>
                      <div style={{ fontWeight: 700 }}>{card.title}</div>
                      <div style={{ fontSize: 13, color: '#475569' }}>{card.body}</div>
                    </div>
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {card.cta} <span aria-hidden>→</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // ✅ clicking top tabs (labels stay “Clients / Providers”)
  const handleExperienceChange = (next: 'client' | 'provider') => {
    setExperience(next)
    persistExperience(next)
    if (next === 'client') {
      nav('/clients')
    } else {
      nav('/dashboard')
    }
  }

  return (
    <AccountAvatarContext.Provider
      value={{ accountAvatarUrl, setAccountAvatarUrl, accountUserId }}
    >
    <NotificationsProvider
      navRole={navRole}
      canPatient={canPatient}
      canSpecialist={canSpecialist}
      enabled={Boolean(user) && !profile?.needsRegistration}
    >
      <>
        {user && (
          <ExperienceSwitcher
            experience={experience}
            onChange={handleExperienceChange}
          />
        )}
        <div className="global-language-float" data-no-i18n>
          <label className="global-language-pill" aria-label="Select language">
            <Globe2 size={16} aria-hidden="true" />
            <select
              aria-label="Select language"
              value={language}
              onChange={(event) => setLanguage(event.target.value as UiLanguageCode)}
            >
              {UI_LANGUAGE_OPTIONS.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

      {!online && (
        <div className="offline" role="status" aria-live="polite">
          You’re offline. Changes will retry when connection resumes.
        </div>
      )}

      <Elements stripe={stripePromise}>
        <Routes>
          {/* root → clients */}
          <Route path="/" element={<Navigate to="/clients" replace />} />

          {/* public */}
          <Route path="/login" element={<Login />} />
          <Route path="/oauth/callback" element={<OauthCallback />} />

          {/* ✅ Clients landing (kept) */}
          <Route
            path="/match"
            element={
              <RequireAuth
                loading={loading}
                user={user}
                profile={profile}
                requireCompleteClient
              >
                <MatchRedirect />
              </RequireAuth>
            }
          />
          {/* Direct /clients alias */}
          <Route
            path="/clients"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <ClientsHome />
              </RequireAuth>
            }
          />
          <Route
            path="/ai-search"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <AiSearchPage />
              </RequireAuth>
            }
          />

          <Route
            path="/register/client"
            element={
              <RequireAuth allowUnregistered loading={loading} user={user} profile={profile}>
                <RegisterClient />
              </RequireAuth>
            }
          />
          <Route
            path="/account"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <AccountPage profile={profile} onLogout={onLogout} />
              </RequireAuth>
            }
          />

          {/* ✅ NEW: Profession-aware matching */}
          <Route
            path="/match/:profession"
            element={
              <RequireAuth
                loading={loading}
                user={user}
                profile={profile}
                requireCompleteClient
              >
                <MatchRedirect />
              </RequireAuth>
            }
          />
          <Route
            path="/results/:profession"
            element={<ResultsGate loading={loading} user={user} profile={profile} />}
          />

          {/* Back-compat: old paths map to doctors */}
          <Route
            path="/start"
            element={
              <RequireAuth
                loading={loading}
                user={user}
                profile={profile}
                requireCompleteClient
              >
                <MatchRedirect />
              </RequireAuth>
            }
          />
          <Route
            path="/results"
            element={
              <RequireAuth
                loading={loading}
                user={user}
                profile={profile}
                requireCompleteClient
              >
                <Navigate to="/clients" replace />
              </RequireAuth>
            }
          />
          <Route
            path="/admin/providers"
            element={
              <RequireAuth loading={loading} user={user} profile={profile} allowUnregistered>
                <AdminProviderEditor />
              </RequireAuth>
            }
          />
          {/* Back-compat: old /match/doctor → /match/doctors */}
          <Route
            path="/match/doctor"
            element={<Navigate to="/clients?profession=doctor" replace />}
          />
          {/* Back-compat: old /work routes */}
          <Route path="/work" element={<Navigate to="/professionals" replace />} />
          <Route path="/work/allied-health" element={<Navigate to="/professionals/allied" replace />} />
          <Route path="/work/doctors" element={<Navigate to="/professionals/doctors" replace />} />
          <Route path="/work/education" element={<Navigate to="/professionals/education" replace />} />
          <Route path="/work/property" element={<Navigate to="/professionals/property" replace />} />
          <Route path="/work/tech" element={<Navigate to="/professionals/tech" replace />} />
          <Route path="/work/financial" element={<Navigate to="/professionals/financial" replace />} />
          <Route path="/work/allied" element={<Navigate to="/professionals/allied" replace />} />
          <Route path="/work/legal" element={<Navigate to="/professionals/legal" replace />} />
          <Route path="/work/migration" element={<Navigate to="/professionals/migration" replace />} />

          {/* ✅ Work (providers) home */}
          <Route
            path="/professionals"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <ProvidersHome />
              </RequireAuth>
            }
          />

          {/* ✅ Work → Doctors registration */}
          <Route
            path="/professionals/doctors"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <RegisterDoctor user={user} />
              </RequireAuth>
            }
          />
          <Route
            path="/professionals/education"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <RegisterEducation user={user} />
              </RequireAuth>
            }
          />
          <Route
            path="/professionals/property"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <RegisterProperty user={user} />
              </RequireAuth>
            }
          />
          <Route
            path="/professionals/tech"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <RegisterTech user={user} />
              </RequireAuth>
            }
          />
          <Route
            path="/professionals/financial"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <RegisterFinancial user={user} />
              </RequireAuth>
            }
          />
          <Route
            path="/professionals/allied"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <RegisterAllied user={user} />
              </RequireAuth>
            }
          />
          <Route
            path="/professionals/legal"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <RegisterLegal user={user} />
              </RequireAuth>
            }
          />
          <Route
            path="/professionals/migration"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <RegisterMigration user={user} />
              </RequireAuth>
            }
          />

          {/* shared authenticated pages */}
          <Route
            path="/dashboard"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <Inbox />
              </RequireAuth>
            }
          />
          <Route
            path="/dashboard/:selectionId"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <InboxDetail />
              </RequireAuth>
            }
          />
          <Route path="/inbox" element={<DashboardRedirect />} />
          <Route path="/inbox/:selectionId" element={<DashboardRedirect />} />

          <Route
            path="/calendar"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <AvailabilityPage />
              </RequireAuth>
            }
          />
          <Route path="/availability" element={<AvailabilityRedirect />} />

          <Route
            path="/appointments"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <AppointmentsPage role={experience === 'client' ? 'patient' : effectiveRole} />
              </RequireAuth>
            }
          />
          <Route
            path="/appointments/:appointmentId"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <AppointmentsPage role={experience === 'client' ? 'patient' : effectiveRole} />
              </RequireAuth>
            }
          />
          <Route
            path="/clientdashboard"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <AppointmentsPage role="patient" />
              </RequireAuth>
            }
          />
          <Route
            path="/clientdashboard/:appointmentId"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <AppointmentsPage role="patient" />
              </RequireAuth>
            }
          />

          {/* Requests (shared) */}
          <Route
            path="/requests"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <RequestsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/requests/:selectionId"
            element={
              <RequireAuth loading={loading} user={user} profile={profile}>
                <RequestsDetail />
              </RequireAuth>
            }
          />

          <Route path="/profile/:providerSlug" element={<ProviderProfilePage />} />
          <Route path="/:providerSlug" element={<ProviderProfilePage />} />
          <Route path="/provider/:providerSlug" element={<ProviderRedirect />} />

          <Route path="/specialist" element={<Navigate to="/account" replace />} />
          <Route path="/patient" element={<Navigate to="/account" replace />} />

          {/* fallback */}
        <Route path="*" element={<Navigate to="/clients" replace />} />
        </Routes>
      </Elements>

        {/* ✅ bottom dock matches current experience,
            incl. when you deep-link to /professionals/doctors */}
        {showBottomDock && <BottomDock experience={experience} />}
        {user && <ChatDock enabled={!profile?.needsRegistration} />}
      </>
    </NotificationsProvider>
    </AccountAvatarContext.Provider>
  )
}
